import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Leaf, AlertCircle, Flag, TrendingUp, Users, AlertTriangle, CheckCircle, Map } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const SEVERITY_COLORS = { High: "bg-red-50 border-red-300 text-red-800", Medium: "bg-amber-50 border-amber-300 text-amber-800", Low: "bg-blue-50 border-blue-300 text-blue-800" };
const PRIORITY_BADGE = { Critical: "bg-red-100 text-red-700", High: "bg-orange-100 text-orange-700", Medium: "bg-yellow-100 text-yellow-700", Low: "bg-sage-100 text-sage-600" };

function Section({ icon: Icon, title, children }) {
  return (
    <div className="bg-white border border-[#E2E8E3] rounded-md mb-4">
      <div className="flex items-center gap-2 px-5 py-3 border-b border-sage-100 bg-sage-50">
        <Icon className="w-4 h-4 text-sage-500" />
        <h3 className="font-heading text-sm font-semibold text-sage-800">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export default function SharedDiagnosticPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/public/share/${token}`)
      .then((res) => setData(res.data))
      .catch((e) => setError(e?.response?.data?.detail || "Shared link not available"))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-sage-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4">
        <div className="bg-white border border-[#E2E8E3] rounded-md p-8 max-w-md text-center" data-testid="share-error">
          <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h2 className="font-heading text-lg font-semibold text-sage-900 mb-1">Link unavailable</h2>
          <p className="text-sage-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const diag = data.diagnostic;
  const org = data.setup?.org_profile || {};
  const vc = data.setup?.volume_capacity || {};

  return (
    <div className="min-h-screen bg-cream">
      <nav className="bg-white border-b border-[#E2E8E3] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Leaf className="w-5 h-5 text-sage-500" />
            <span className="font-heading text-lg font-semibold text-sage-800 tracking-tight">TheKaizenCabinet</span>
            <span className="text-sage-400 text-xs hidden sm:block">Shared Diagnostic</span>
          </div>
          <div className="text-xs text-sage-400">Read-only · {data.expires_at ? `Expires ${new Date(data.expires_at).toLocaleDateString()}` : ""}</div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-6" data-testid="shared-diagnostic-content">
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-1">Lean Workforce Diagnostic</p>
          <h1 className="font-heading text-2xl font-semibold text-sage-900">{diag.org_name || data.company_name}</h1>
          <p className="text-sage-500 text-sm mt-0.5">{diag.domain} · {data.project_name}</p>
        </div>

        <Section icon={Flag} title="Report Overview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Stages Analysed", value: diag.stage_count || 0 },
              { label: "Monthly Volume", value: (diag.total_volume || 0).toLocaleString() },
              { label: "Total FTE", value: diag.summary_stats?.total_current_fte || 0 },
              { label: "Required FTE", value: diag.summary_stats?.total_required_fte || 0 },
              { label: "Headcount Delta", value: `${(diag.summary_stats?.headcount_delta || 0) > 0 ? "+" : ""}${diag.summary_stats?.headcount_delta || 0}` },
              { label: "Potential Saving/Mo", value: `${org.currency || vc.currency || "USD"} ${((diag.summary_stats?.potential_saving || 0) / 1000).toFixed(0)}K` },
              { label: "Monthly Cost", value: `${org.currency || "USD"} ${((diag.summary_stats?.monthly_cost || 0) / 1000).toFixed(0)}K` },
              { label: "Generated", value: diag.generated_at ? new Date(diag.generated_at).toLocaleDateString() : "—" },
            ].map(({ label, value }) => (
              <div key={label} className="bg-sage-50 rounded-md p-3">
                <p className="text-xs text-sage-500 uppercase tracking-wider font-semibold mb-0.5">{label}</p>
                <p className="font-heading text-base font-bold text-sage-900">{value}</p>
              </div>
            ))}
          </div>
        </Section>

        {diag.workforce_intel && (
          <Section icon={Users} title="Workforce Composition">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { label: "Local Nationals", value: `${diag.workforce_intel.local_count} (${diag.workforce_intel.local_pct}%)` },
                { label: "Expatriates", value: `${diag.workforce_intel.expat_count}` },
                { label: "Conversion Factor", value: `1 local = ${diag.workforce_intel.conversion_factor}× expat` },
                { label: "Local Hrs/Month", value: diag.workforce_intel.monthly_hrs_local },
                { label: "Expat Hrs/Month", value: diag.workforce_intel.monthly_hrs_expat },
                { label: "Localisation Status", value: diag.workforce_intel.localization_status?.replace("_", " ") || "—" },
              ].map(({ label, value }) => (
                <div key={label} className="bg-sage-50 rounded-md p-3">
                  <p className="text-xs text-sage-500 uppercase tracking-wider font-semibold mb-0.5">{label}</p>
                  <p className="font-heading text-sm font-bold text-sage-900 capitalize">{value}</p>
                </div>
              ))}
            </div>
          </Section>
        )}

        {diag.priority_actions?.length > 0 && (
          <Section icon={TrendingUp} title="Priority Focus Areas — Top 3 Stages">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {diag.priority_actions.map((stage, i) => (
                <div key={i} className="border border-sage-200 rounded-md p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 bg-sage-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">#{i + 1}</span>
                    <h4 className="font-heading text-sm font-semibold text-sage-900">{stage.name}</h4>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div><span className="text-sage-400">Req FTE: </span><span className="font-semibold text-sage-800">{stage.required_fte}</span></div>
                    <div><span className="text-sage-400">Current: </span><span className="font-semibold text-sage-800">{stage.current_fte}</span></div>
                    <div><span className="text-sage-400">Yield: </span><span className="font-semibold text-sage-800">{stage.yield_pct}%</span></div>
                    <div><span className="text-sage-400">AHT: </span><span className="font-semibold text-sage-800">{stage.aht}m</span></div>
                  </div>
                  <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_BADGE[stage.priority] || PRIORITY_BADGE.Medium}`}>{stage.priority}</span>
                </div>
              ))}
            </div>
          </Section>
        )}

        {diag.alerts?.length > 0 && (
          <Section icon={AlertTriangle} title="Waste Diagnostic Alerts">
            <div className="space-y-3">
              {diag.alerts.map((alert, i) => (
                <div key={i} className={`rounded-md p-4 border ${SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.Medium}`}>
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-semibold text-sm">{alert.type}: {alert.value}%</h4>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${alert.severity === "High" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{alert.severity}</span>
                  </div>
                  <p className="text-xs leading-relaxed opacity-90">{alert.message}</p>
                </div>
              ))}
            </div>
          </Section>
        )}

        {diag.lean_levers?.length > 0 && (
          <Section icon={TrendingUp} title={`Domain Lean Levers — ${diag.domain}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {diag.lean_levers.map((lever, i) => (
                <div key={i} className="border border-sage-200 rounded-md p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-heading text-sm font-semibold text-sage-900">{lever.title}</h4>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ml-2 ${PRIORITY_BADGE[lever.priority] || PRIORITY_BADGE.Medium}`}>{lever.priority}</span>
                  </div>
                  <p className="text-xs text-sage-600 leading-relaxed">{lever.body}</p>
                </div>
              ))}
            </div>
          </Section>
        )}

        {diag.roadmap?.length > 0 && (
          <Section icon={Map} title="Lean Programme Roadmap">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {diag.roadmap.map((phase) => (
                <div key={phase.phase} className="border border-[#E2E8E3] rounded-md p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 bg-sage-600 text-white rounded-full flex items-center justify-center text-xs font-bold">{phase.phase}</span>
                    <span className="text-xs bg-sage-100 text-sage-600 px-2 py-0.5 rounded-full font-medium">{phase.months} mo</span>
                  </div>
                  <h4 className="font-heading text-sm font-semibold text-sage-900 mb-1">{phase.title}</h4>
                  <p className="text-xs text-sage-500 mb-3 leading-relaxed">{phase.description}</p>
                  <ul className="space-y-1">
                    {phase.actions?.map((a, ai) => (
                      <li key={ai} className="flex items-start gap-1.5 text-xs text-sage-600">
                        <CheckCircle className="w-3 h-3 mt-0.5 text-sage-400 flex-shrink-0" /> {a}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Section>
        )}

        <p className="text-center text-xs text-sage-400 pt-4 pb-8">Generated by TheKaizenCabinet · Read-only share</p>
      </main>
    </div>
  );
}
